import type { Cell } from '../game/model/types';
import {
  cellKey,
  directionBetween,
  isAhead,
  isTurn,
} from '../game/model/cells';
import type { BoardMask } from './mask';
import {
  chainPatchArrowCount,
  chainPatchArrowLength,
  chainPatchCornerMargin,
  chainTrapNormalEnforcementCellThreshold,
  largeRectangleHamiltonianPartitionThreshold,
  largeRectangleShortPathMaximumLength,
  largeRectangleShortPathMergeThreshold,
  largeRectangleShortPathRatioMaximum,
  largeRectangleShortPathRatioMinimum,
  partitionRestartLimit,
  snakeAttemptLimit,
} from './curve';
import { nextInt, pickOne, popRandom, shuffle, type Rng } from './rng';
import type { GenerationParams } from './types';

export type SnakePath = readonly Cell[];

export interface PartitionEffort {
  count: number;
}

export interface PartitionResult {
  readonly paths: readonly SnakePath[];
  readonly ringMotifCount: number;
  readonly ringEnclosedCellCount: number;
  readonly chainPatchPathCount: number;
}

export interface RectangleRegion {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface WeightedCandidate<T> {
  readonly value: T;
  readonly weight: number;
}

interface PreparedLengthMix {
  readonly weighted: readonly WeightedCandidate<GenerationParams['lengthMix'][number]>[];
  readonly totalWeight: number;
  readonly fallback: GenerationParams['lengthMix'][number];
  readonly minimum: number;
  readonly maximum: number;
}

type HalfGridDirectionIndex = 0 | 1 | 2 | 3;

interface HalfGridTreeEdge {
  readonly from: number;
  readonly to: number;
  readonly direction: HalfGridDirectionIndex;
}

const offsets = [[0, -1], [1, 0], [0, 1], [-1, 0]] as const;

function neighbors(cell: Cell, available: ReadonlySet<string>): Cell[] {
  return offsets
    .map(([dx, dy]) => ({ x: cell.x + dx, y: cell.y + dy }))
    .filter((candidate) => available.has(cellKey(candidate)));
}

export function isSelfRayValid(
  path: SnakePath,
  tipAtStart: boolean,
  lengthLimit = path.length,
): boolean {
  const length = Math.min(path.length, lengthLimit);
  const tip = tipAtStart ? path[0] : path[length - 1];
  const beforeTip = tipAtStart ? path[1] : path[length - 2];
  if (tip === undefined || beforeTip === undefined) {
    return false;
  }
  const direction = directionBetween(beforeTip, tip);
  for (let index = 0; index < length; index += 1) {
    const cell = path[index];
    if (cell !== undefined && cell !== tip && isAhead(tip, cell, direction)) {
      return false;
    }
  }
  return true;
}

export function hasNoSmallComponent(
  available: ReadonlySet<string>,
  cellsByKey: ReadonlyMap<string, Cell>,
  minimum: number,
): boolean {
  const visited = new Set<string>();
  for (const firstKey of available) {
    if (visited.has(firstKey)) {
      continue;
    }
    visited.add(firstKey);
    const discovered = new Set([firstKey]);
    const pending = [cellsByKey.get(firstKey)];
    let size = 1;
    let joinsKnownLargeComponent = false;
    while (pending.length > 0 && size < minimum && !joinsKnownLargeComponent) {
      const cell = pending.pop();
      if (cell === undefined) {
        continue;
      }
      // This is a hot path, so expand adjacent cells inline instead of using neighbors().
      for (const [dx, dy] of offsets) {
        const neighborKey = `${cell.x + dx}:${cell.y + dy}`;
        if (!available.has(neighborKey) || discovered.has(neighborKey)) {
          continue;
        }
        if (visited.has(neighborKey)) {
          // A previous traversal is recorded only after reaching minimum, so
          // touching it proves this remainder belongs to that large component.
          joinsKnownLargeComponent = true;
          break;
        }
        visited.add(neighborKey);
        discovered.add(neighborKey);
        size += 1;
        pending.push(cellsByKey.get(neighborKey));
      }
    }
    if (size < minimum && !joinsKnownLargeComponent) {
      return false;
    }
  }
  return true;
}

function pickWeighted<T>(
  rng: Rng,
  candidates: readonly WeightedCandidate<T>[],
  totalWeight: number,
): T | undefined {
  if (totalWeight <= 0) {
    return undefined;
  }
  let sample = rng() * totalWeight;
  for (const candidate of candidates) {
    sample -= candidate.weight;
    if (sample < 0) {
      return candidate.value;
    }
  }
  return candidates.at(-1)?.value;
}

function totalCandidateWeight<T>(candidates: readonly WeightedCandidate<T>[]): number {
  return candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
}

function weightedNext(
  rng: Rng,
  candidates: readonly Cell[],
  current: Cell,
  previous: Cell | undefined,
  available: ReadonlySet<string>,
  turnBias: number,
): Cell {
  const candidatesWithTurns = candidates.map((candidate) => ({
    candidate,
    turning: previous !== undefined && isTurn(previous, current, candidate),
  }));
  const turns = candidatesWithTurns.filter(({ turning }) => turning).length;
  const straights = candidates.length - turns;
  const weighted = candidatesWithTurns.map(({ candidate, turning }) => {
    const directionWeight = previous === undefined
      ? 1
      : turning
        ? turnBias / Math.max(1, turns)
        : (1 - turnBias) / Math.max(1, straights);
    return {
      value: candidate,
      weight: directionWeight * (5 - neighbors(candidate, available).length),
    };
  });
  const total = weighted.reduce((sum, entry) => sum + entry.weight, 0);
  return pickWeighted(rng, weighted, total) ?? pickOne(rng, candidates);
}

function grow(
  rng: Rng,
  initial: readonly Cell[],
  free: ReadonlySet<string>,
  targetLength: number,
  turnBias: number,
): Cell[] {
  const path = [...initial];
  const available = new Set(free);
  path.forEach((cell) => available.delete(cellKey(cell)));
  while (path.length < targetLength) {
    const current = path.at(-1);
    if (current === undefined) {
      break;
    }
    const candidates = neighbors(current, available);
    if (candidates.length === 0) {
      break;
    }
    const next = weightedNext(rng, candidates, current, path.at(-2), available, turnBias);
    path.push(next);
    available.delete(cellKey(next));
  }
  return path;
}

export function chooseUsablePrefix(
  path: readonly Cell[],
  free: ReadonlySet<string>,
  cellsByKey: ReadonlyMap<string, Cell>,
  minimum: number,
): Cell[] | null {
  const remaining = new Set(free);
  path.forEach((cell) => remaining.delete(cellKey(cell)));
  for (let length = path.length; length >= minimum; length -= 1) {
    if ((isSelfRayValid(path, false, length) || isSelfRayValid(path, true, length))
      && hasNoSmallComponent(remaining, cellsByKey, minimum)) {
      return path.slice(0, length);
    }
    const removedTail = path[length - 1];
    if (removedTail !== undefined) {
      const removedTailKey = cellKey(removedTail);
      remaining.add(removedTailKey);
    }
  }
  return null;
}

export function tryMergePaths(
  leftPath: readonly Cell[],
  rightPath: readonly Cell[],
  maxLength: number,
): Cell[] | null {
  if (leftPath.length + rightPath.length > maxLength) {
    return null;
  }
  for (const left of [leftPath, [...leftPath].reverse()]) {
    for (const right of [rightPath, [...rightPath].reverse()]) {
      const leftEnd = left.at(-1);
      const rightStart = right[0];
      if (leftEnd === undefined || rightStart === undefined
        || Math.abs(leftEnd.x - rightStart.x) + Math.abs(leftEnd.y - rightStart.y) !== 1) {
        continue;
      }
      const merged = [...left, ...right];
      if (isSelfRayValid(merged, false) || isSelfRayValid(merged, true)) {
        return merged;
      }
    }
  }
  return null;
}

function tryMergeShortPath(
  paths: readonly Cell[][],
  shortPath: readonly Cell[],
  maximum: number,
): { readonly index: number; readonly path: Cell[] } | null {
  for (let index = paths.length - 1; index >= 0; index -= 1) {
    const existing = paths[index];
    if (existing === undefined) {
      continue;
    }
    const merged = tryMergePaths(existing, shortPath, maximum);
    if (merged !== null) {
      return { index, path: merged };
    }
  }
  return null;
}

function validateParams(params: GenerationParams): void {
  if (params.lengthMix.length === 0) {
    throw new Error('lengthMix must not be empty.');
  }
  for (const [minimum, maximum, weight] of params.lengthMix) {
    if (!Number.isInteger(minimum) || !Number.isInteger(maximum)
      || minimum < 2 || maximum < minimum
      || !Number.isFinite(weight) || weight <= 0) {
      throw new Error(
        'lengthMix entries must contain integers with 2 <= min <= max and a positive finite weight.',
      );
    }
  }
  if (params.turnBias < 0 || params.turnBias > 1 || !Number.isFinite(params.turnBias)) {
    throw new Error('turnBias must be between 0 and 1.');
  }
  if (!Number.isInteger(params.orientationPasses) || params.orientationPasses < 1) {
    throw new Error('orientationPasses must be a positive integer.');
  }
  const vacancy = params.vacancy;
  if (vacancy !== undefined) {
    const [minimumDeletions, maximumDeletions] = vacancy.deletionCount;
    if (!Number.isInteger(minimumDeletions) || !Number.isInteger(maximumDeletions)
      || minimumDeletions < 0 || maximumDeletions < minimumDeletions) {
      throw new Error('vacancy deletionCount must be an ordered non-negative integer range.');
    }
    if (!Number.isFinite(vacancy.longRangeDistance) || vacancy.longRangeDistance <= 0) {
      throw new Error('vacancy longRangeDistance must be a positive finite number.');
    }
  }
  const chainTrap = params.chainTrap;
  if (chainTrap !== undefined) {
    const [minimumTraps, maximumTraps] = chainTrap.trapCountTarget;
    const [minimumRate, maximumRate] = chainTrap.initialRemovableRate;
    if (!Number.isInteger(chainTrap.orientationCandidates)
      || chainTrap.orientationCandidates < 1) {
      throw new Error('chainTrap orientationCandidates must be a positive integer.');
    }
    if (!Number.isInteger(chainTrap.chainRunTarget) || chainTrap.chainRunTarget < 0) {
      throw new Error('chainTrap chainRunTarget must be a non-negative integer.');
    }
    if (!Number.isInteger(minimumTraps) || !Number.isInteger(maximumTraps)
      || minimumTraps < 0 || maximumTraps < minimumTraps
      || !Number.isInteger(chainTrap.maximumTrapCount)
      || chainTrap.maximumTrapCount < maximumTraps) {
      throw new Error('chainTrap trap counts must be ordered non-negative integers.');
    }
    if (!Number.isFinite(minimumRate) || !Number.isFinite(maximumRate)
      || minimumRate <= 0 || maximumRate >= 1 || maximumRate < minimumRate) {
      throw new Error('chainTrap initialRemovableRate must be ordered within zero and one.');
    }
  }
  const warden = params.warden;
  if (warden !== undefined) {
    const integerRangeIsValid = ([minimum, maximum]: readonly [number, number]): boolean =>
      Number.isInteger(minimum) && Number.isInteger(maximum) && minimum >= 1 && maximum >= minimum;
    if (!integerRangeIsValid(warden.count)) {
      throw new Error('warden count must be an ordered positive integer range.');
    }
    if (!Number.isInteger(warden.minimumLength) || warden.minimumLength < 2) {
      throw new Error('warden minimumLength must be an integer of at least two.');
    }
    if (!Number.isInteger(warden.orientationCandidates) || warden.orientationCandidates < 1) {
      throw new Error('warden orientationCandidates must be a positive integer.');
    }
    if (!Number.isFinite(warden.initialRemovableRateTarget)
      || warden.initialRemovableRateTarget <= 0
      || warden.initialRemovableRateTarget >= 1) {
      throw new Error('warden initialRemovableRateTarget must be between zero and one.');
    }
    if (!Number.isInteger(warden.peelDepthTarget) || warden.peelDepthTarget < 1) {
      throw new Error('warden peelDepthTarget must be a positive integer.');
    }
    if (!Number.isFinite(warden.longBlockerRatioTarget)
      || warden.longBlockerRatioTarget <= 0
      || warden.longBlockerRatioTarget > 1) {
      throw new Error('warden longBlockerRatioTarget must be greater than zero and at most one.');
    }
    if (!Number.isFinite(warden.mergeLengthMultiplier) || warden.mergeLengthMultiplier < 1) {
      throw new Error('warden mergeLengthMultiplier must be at least one.');
    }
    if (!Number.isInteger(warden.victimChunkLength) || warden.victimChunkLength < 2) {
      throw new Error('warden victimChunkLength must be an integer of at least two.');
    }
    if (!Number.isInteger(warden.maximumVictimChunks) || warden.maximumVictimChunks < 2) {
      throw new Error('warden maximumVictimChunks must be an integer of at least two.');
    }
    if (!Number.isFinite(warden.victimSplitPreference)
      || warden.victimSplitPreference < 0
      || warden.victimSplitPreference > 1) {
      throw new Error('warden victimSplitPreference must be between zero and one.');
    }
    if (!Number.isInteger(warden.candidatePoolMultiplier)
      || warden.candidatePoolMultiplier < 1) {
      throw new Error('warden candidatePoolMultiplier must be a positive integer.');
    }
    for (const [name, preference] of [
      ['selectionPreference', warden.selectionPreference],
      ['victimOrientationPreference', warden.victimOrientationPreference],
      ['chainOrientationPreference', warden.chainOrientationPreference],
    ] as const) {
      if (!Number.isFinite(preference) || preference < 0 || preference > 1) {
        throw new Error(`warden ${name} must be between zero and one.`);
      }
    }
  }
}

function sampleTargetLength(
  weightedLengthMix: readonly WeightedCandidate<GenerationParams['lengthMix'][number]>[],
  totalWeight: number,
  fallback: GenerationParams['lengthMix'][number],
  rng: Rng,
): number {
  const [minimum, maximum] = pickWeighted(
    rng,
    weightedLengthMix,
    totalWeight,
  ) ?? fallback;
  return nextInt(rng, minimum, maximum);
}

function prepareLengthMix(params: GenerationParams): PreparedLengthMix {
  const weighted = params.lengthMix.map((entry) => ({ value: entry, weight: entry[2] }));
  return {
    weighted,
    totalWeight: totalCandidateWeight(weighted),
    fallback: params.lengthMix.at(-1) ?? params.lengthMix[0]!,
    minimum: Math.min(...params.lengthMix.map(([minimum]) => minimum)),
    maximum: Math.max(...params.lengthMix.map(([, maximum]) => maximum)),
  };
}

function edgeChainPatchPaths(
  mask: BoardMask,
  params: GenerationParams,
  rng: Rng,
): readonly SnakePath[] {
  if ((params.chainTrap?.chainRunTarget ?? 0) === 0
    || mask.cells.length !== mask.width * mask.height
    || (params.warden === undefined
      && mask.cells.length < chainTrapNormalEnforcementCellThreshold)
    || (params.warden !== undefined
      && mask.cells.length <= largeRectangleHamiltonianPartitionThreshold)
    || mask.width < chainPatchArrowLength + chainPatchCornerMargin * 2
    || mask.height < chainPatchArrowLength + chainPatchCornerMargin * 2) {
    return [];
  }
  const side = nextInt(rng, 0, 3);
  const horizontalEdge = side < 2;
  const edgeLength = horizontalEdge ? mask.width : mask.height;
  const minimumOffset = chainPatchCornerMargin;
  const maximumOffset = edgeLength - chainPatchArrowCount - chainPatchCornerMargin;
  const offset = nextInt(rng, minimumOffset, maximumOffset);
  return Array.from({ length: chainPatchArrowCount }, (_, line): Cell[] => {
    if (side === 0) {
      const x = offset + line;
      return Array.from({ length: chainPatchArrowLength }, (_, depth) => ({
        x,
        y: chainPatchArrowLength - depth - 1,
      }));
    }
    if (side === 1) {
      const x = offset + line;
      return Array.from({ length: chainPatchArrowLength }, (_, depth) => ({
        x,
        y: mask.height - chainPatchArrowLength + depth,
      }));
    }
    if (side === 2) {
      const y = offset + line;
      return Array.from({ length: chainPatchArrowLength }, (_, depth) => ({
        x: chainPatchArrowLength - depth - 1,
        y,
      }));
    }
    const y = offset + line;
    return Array.from({ length: chainPatchArrowLength }, (_, depth) => ({
      x: mask.width - chainPatchArrowLength + depth,
      y,
    }));
  });
}

function randomHalfGridSpanningTree(
  width: number,
  height: number,
  turnBias: number,
  rng: Rng,
): readonly HalfGridTreeEdge[] {
  const nodeCount = width * height;
  const visited = Array.from({ length: nodeCount }, () => false);
  const parentDirections: Array<HalfGridDirectionIndex | undefined> = Array.from(
    { length: nodeCount },
  );
  const straightFrontier: HalfGridTreeEdge[] = [];
  const turningFrontier: HalfGridTreeEdge[] = [];
  const tree: HalfGridTreeEdge[] = [];
  const root = nextInt(rng, 0, nodeCount - 1);
  visited[root] = true;

  const addFrontier = (from: number): void => {
    const x = from % width;
    const y = Math.floor(from / width);
    const parentDirection = parentDirections[from];
    for (let directionIndex = 0; directionIndex < offsets.length; directionIndex += 1) {
      const [dx, dy] = offsets[directionIndex]!;
      const direction = directionIndex as HalfGridDirectionIndex;
      const nextX = x + dx;
      const nextY = y + dy;
      if (nextX < 0 || nextX >= width || nextY < 0 || nextY >= height) {
        continue;
      }
      const to = nextY * width + nextX;
      if (visited[to]) {
        continue;
      }
      const edge = { from, to, direction } satisfies HalfGridTreeEdge;
      (parentDirection !== undefined && direction !== parentDirection
        ? turningFrontier
        : straightFrontier).push(edge);
    }
  };
  addFrontier(root);

  const popPreferred = (preferTurning: boolean): HalfGridTreeEdge | undefined =>
    popRandom(rng, preferTurning ? turningFrontier : straightFrontier)
      ?? popRandom(rng, preferTurning ? straightFrontier : turningFrontier);

  while (tree.length < nodeCount - 1) {
    const preferTurning = rng() < turnBias;
    let selected = popPreferred(preferTurning);
    while (selected !== undefined && visited[selected.to]) {
      selected = popPreferred(preferTurning);
    }
    if (selected === undefined) {
      throw new Error('Randomized Prim frontier exhausted before spanning the half grid.');
    }
    visited[selected.to] = true;
    parentDirections[selected.to] = selected.direction;
    tree.push(selected);
    addFrontier(selected.to);
  }
  return tree;
}

function hamiltonianCycleFromTree(
  fullWidth: number,
  fullHeight: number,
  tree: readonly HalfGridTreeEdge[],
  rng: Rng,
): readonly Cell[] {
  const halfWidth = fullWidth / 2;
  const adjacency = Array.from(
    { length: fullWidth * fullHeight },
    () => new Set<number>(),
  );
  const indexFor = (x: number, y: number): number => y * fullWidth + x;
  const cellFor = (index: number): Cell => ({
    x: index % fullWidth,
    y: Math.floor(index / fullWidth),
  });
  const connect = (left: number, right: number): void => {
    adjacency[left]?.add(right);
    adjacency[right]?.add(left);
  };
  const disconnect = (left: number, right: number): void => {
    adjacency[left]?.delete(right);
    adjacency[right]?.delete(left);
  };

  for (let blockY = 0; blockY < fullHeight / 2; blockY += 1) {
    for (let blockX = 0; blockX < halfWidth; blockX += 1) {
      const topLeft = indexFor(2 * blockX, 2 * blockY);
      const topRight = indexFor(2 * blockX + 1, 2 * blockY);
      const bottomLeft = indexFor(2 * blockX, 2 * blockY + 1);
      const bottomRight = indexFor(2 * blockX + 1, 2 * blockY + 1);
      connect(topLeft, topRight);
      connect(topRight, bottomRight);
      connect(bottomRight, bottomLeft);
      connect(bottomLeft, topLeft);
    }
  }

  for (const edge of tree) {
    const fromX = edge.from % halfWidth;
    const fromY = Math.floor(edge.from / halfWidth);
    const toX = edge.to % halfWidth;
    const toY = Math.floor(edge.to / halfWidth);
    if (fromY === toY) {
      const leftX = Math.min(fromX, toX);
      const leftTop = indexFor(2 * leftX + 1, 2 * fromY);
      const leftBottom = indexFor(2 * leftX + 1, 2 * fromY + 1);
      const rightTop = indexFor(2 * leftX + 2, 2 * fromY);
      const rightBottom = indexFor(2 * leftX + 2, 2 * fromY + 1);
      disconnect(leftTop, leftBottom);
      disconnect(rightTop, rightBottom);
      connect(leftTop, rightTop);
      connect(leftBottom, rightBottom);
    } else {
      const topY = Math.min(fromY, toY);
      const topLeft = indexFor(2 * fromX, 2 * topY + 1);
      const topRight = indexFor(2 * fromX + 1, 2 * topY + 1);
      const bottomLeft = indexFor(2 * fromX, 2 * topY + 2);
      const bottomRight = indexFor(2 * fromX + 1, 2 * topY + 2);
      disconnect(topLeft, topRight);
      disconnect(bottomLeft, bottomRight);
      connect(topLeft, bottomLeft);
      connect(topRight, bottomRight);
    }
  }

  const outwardStarts = adjacency.flatMap((neighbors, start) => {
    const startCell = cellFor(start);
    return [...neighbors]
      .filter((next) => {
        const nextCell = cellFor(next);
        return (startCell.x === 0 && nextCell.x === 1)
          || (startCell.x === fullWidth - 1 && nextCell.x === fullWidth - 2)
          || (startCell.y === 0 && nextCell.y === 1)
          || (startCell.y === fullHeight - 1 && nextCell.y === fullHeight - 2);
      })
      .map((next) => ({ start, next }));
  });
  if (outwardStarts.length === 0) {
    throw new Error('Hamiltonian cycle has no outward-facing boundary start.');
  }
  const { start, next: first } = pickOne(rng, outwardStarts);
  const cycle: Cell[] = [cellFor(start)];
  let previous = start;
  let current = first;
  while (current !== start) {
    cycle.push(cellFor(current));
    let next: number | undefined;
    for (const candidate of adjacency[current] ?? []) {
      if (candidate !== previous) {
        next = candidate;
        break;
      }
    }
    if (next === undefined) {
      throw new Error(`Hamiltonian cycle traversal stopped at vertex ${current}.`);
    }
    previous = current;
    current = next;
  }
  if (cycle.length !== fullWidth * fullHeight) {
    throw new Error(`Hamiltonian cycle covers ${cycle.length}/${fullWidth * fullHeight} cells.`);
  }
  return cycle;
}

function partitionHamiltonianRectangle(
  region: RectangleRegion,
  params: GenerationParams,
  rng: Rng,
  effort: PartitionEffort,
  effortLimit: number,
  reservedPaths: readonly SnakePath[],
): readonly SnakePath[] | null {
  if (region.width % 2 !== 0 || region.height % 2 !== 0) {
    throw new Error(
      `Hamiltonian rectangle dimensions must both be even: ${region.width}x${region.height}.`,
    );
  }
  const tree = randomHalfGridSpanningTree(
    region.width / 2,
    region.height / 2,
    params.turnBias,
    rng,
  );
  const cells = hamiltonianCycleFromTree(region.width, region.height, tree, rng)
    .map((cell) => ({ x: cell.x + region.x, y: cell.y + region.y }));
  let paths: Cell[][] = reservedPaths.map((path) => [...path]);
  const protectedPathCount = paths.length;
  const {
    weighted: weightedLengthMix,
    totalWeight,
    fallback: fallbackLengthMix,
    minimum: minimumLength,
    maximum: maximumLength,
  } = prepareLengthMix(params);
  const hasShortPathBand = params.lengthMix.some(
    ([minimum]) => minimum <= largeRectangleShortPathMaximumLength,
  );
  const cellCount = cells.length;
  const active = Array.from({ length: cellCount }, () => true);
  const cycleIndexByGridIndex = Array.from({ length: cellCount }, () => -1);
  const cycleIndexAt = (x: number, y: number): number =>
    cycleIndexByGridIndex[(y - region.y) * region.width + x - region.x] ?? -1;
  const cycleIndexOf = (cell: Cell): number => cycleIndexAt(cell.x, cell.y);
  cells.forEach((cell, cycleIndex) => {
    cycleIndexByGridIndex[(cell.y - region.y) * region.width + cell.x - region.x] = cycleIndex;
  });
  const rowMinimum = Array.from({ length: region.height }, () => region.x);
  const rowMaximum = Array.from({ length: region.height }, () => region.x + region.width - 1);
  const columnMinimum = Array.from({ length: region.width }, () => region.y);
  const columnMaximum = Array.from({ length: region.width }, () => region.y + region.height - 1);
  const candidateIds: number[] = [];
  const candidatePositions = Array.from({ length: cellCount * 2 }, () => -1);
  const wrapIndex = (index: number): number => ((index % cellCount) + cellCount) % cellCount;
  const candidateDirection = (candidateId: number): -1 | 1 =>
    candidateId % 2 === 0 ? -1 : 1;
  const addCandidate = (candidateId: number): void => {
    if (candidatePositions[candidateId] !== -1) return;
    candidatePositions[candidateId] = candidateIds.length;
    candidateIds.push(candidateId);
  };
  const swapRemoveCandidateAt = (position: number): number | undefined => {
    const candidateId = candidateIds[position];
    const last = candidateIds.pop();
    if (candidateId === undefined) return undefined;
    candidatePositions[candidateId] = -1;
    if (last !== undefined && position < candidateIds.length) {
      candidateIds[position] = last;
      candidatePositions[last] = position;
    }
    return candidateId;
  };
  const removeCandidate = (candidateId: number): void => {
    const position = candidatePositions[candidateId];
    if (position === undefined || position < 0) return;
    swapRemoveCandidateAt(position);
  };
  const popRandomCandidate = (): number | undefined => {
    if (candidateIds.length === 0) return undefined;
    const position = nextInt(rng, 0, candidateIds.length - 1);
    return swapRemoveCandidateAt(position);
  };
  const refreshCandidate = (cycleIndex: number): void => {
    if (cycleIndex < 0) return;
    for (const direction of [-1, 1] as const) {
      const candidateId = cycleIndex * 2 + (direction === -1 ? 0 : 1);
      removeCandidate(candidateId);
      const nextIndex = wrapIndex(cycleIndex + direction);
      const tip = cells[cycleIndex];
      const next = cells[nextIndex];
      if (!active[cycleIndex] || !active[nextIndex] || tip === undefined || next === undefined) {
        continue;
      }
      const dx = tip.x - next.x;
      const dy = tip.y - next.y;
      const rowIndex = tip.y - region.y;
      const columnIndex = tip.x - region.x;
      const exposed = (dx === -1 && tip.x === rowMinimum[rowIndex])
        || (dx === 1 && tip.x === rowMaximum[rowIndex])
        || (dy === -1 && tip.y === columnMinimum[columnIndex])
        || (dy === 1 && tip.y === columnMaximum[columnIndex]);
      if (exposed) addCandidate(candidateId);
    }
  };
  const refreshGridCell = (x: number, y: number): void => {
    if (x < region.x || x >= region.x + region.width
      || y < region.y || y >= region.y + region.height) return;
    refreshCandidate(cycleIndexAt(x, y));
  };
  const advanceBoundary = (
    cellCoordinate: number,
    fixedIndex: number,
    boundaries: number[],
    lowerBound: number,
    upperBound: number,
    step: -1 | 1,
    row: boolean,
  ): void => {
    if (cellCoordinate !== boundaries[fixedIndex]) return;
    const fixedCoordinate = row ? region.y + fixedIndex : region.x + fixedIndex;
    while (boundaries[fixedIndex]! >= lowerBound && boundaries[fixedIndex]! < upperBound) {
      const boundary = boundaries[fixedIndex]!;
      const index = row
        ? cycleIndexAt(boundary, fixedCoordinate)
        : cycleIndexAt(fixedCoordinate, boundary);
      if (active[index]) break;
      boundaries[fixedIndex]! += step;
    }
    if (row) {
      refreshGridCell(boundaries[fixedIndex]!, fixedCoordinate);
    } else {
      refreshGridCell(fixedCoordinate, boundaries[fixedIndex]!);
    }
  };
  const deactivate = (cycleIndex: number): void => {
    const cell = cells[cycleIndex];
    if (cell === undefined || !active[cycleIndex]) return;
    active[cycleIndex] = false;
    refreshCandidate(cycleIndex);
    refreshCandidate(wrapIndex(cycleIndex - 1));
    refreshCandidate(wrapIndex(cycleIndex + 1));

    advanceBoundary(
      cell.x, cell.y - region.y, rowMinimum, region.x, region.x + region.width, 1, true,
    );
    advanceBoundary(
      cell.x, cell.y - region.y, rowMaximum, region.x, region.x + region.width, -1, true,
    );
    advanceBoundary(
      cell.y, cell.x - region.x, columnMinimum, region.y, region.y + region.height, 1, false,
    );
    advanceBoundary(
      cell.y, cell.x - region.x, columnMaximum, region.y, region.y + region.height, -1, false,
    );
  };
  const residueIsValid = (
    firstIndex: number,
    direction: -1 | 1,
    selectedStart: number,
    selectedDirection: -1 | 1,
    selectedLength: number,
  ): boolean => {
    const isSelected = (index: number): boolean => {
      const distance = selectedDirection === 1
        ? wrapIndex(index - selectedStart)
        : wrapIndex(selectedStart - index);
      return distance < selectedLength;
    };
    let size = 0;
    let index = firstIndex;
    while (size < minimumLength && active[index] && !isSelected(index)) {
      size += 1;
      index = wrapIndex(index + direction);
    }
    return size === 0 || size >= minimumLength;
  };
  const candidatePath = (
    candidateId: number,
    sampledLength: number,
  ): { readonly indexes: readonly number[]; readonly cells: readonly Cell[] } | undefined => {
    const start = Math.floor(candidateId / 2);
    const direction = candidateDirection(candidateId);
    let availableLength = 0;
    let index = start;
    while (availableLength < sampledLength && availableLength < cellCount && active[index]) {
      availableLength += 1;
      index = wrapIndex(index + direction);
    }
    for (let length = availableLength; length >= minimumLength; length -= 1) {
      const before = wrapIndex(start - direction);
      const after = wrapIndex(start + direction * length);
      if (residueIsValid(before, -direction as -1 | 1, start, direction, length)
        && residueIsValid(after, direction, start, direction, length)) {
        const indexes = Array.from(
          { length },
          (_, offset) => wrapIndex(start + direction * offset),
        );
        const candidateCells = indexes.map((pathIndex) => cells[pathIndex]);
        if (candidateCells.some((cell) => cell === undefined)) {
          throw new Error(
            `Hamiltonian candidate escaped ${region.x},${region.y} ${region.width}x${region.height}.`,
          );
        }
        return { indexes, cells: candidateCells as Cell[] };
      }
    }
    return undefined;
  };

  const reservedCycleIndexes = new Set<number>();
  for (const path of reservedPaths) {
    for (const cell of path) {
      const cycleIndex = cycleIndexOf(cell);
      if (cycleIndex < 0 || reservedCycleIndexes.has(cycleIndex)) return null;
      reservedCycleIndexes.add(cycleIndex);
      deactivate(cycleIndex);
    }
  }
  for (let cycleIndex = 0; cycleIndex < cellCount; cycleIndex += 1) {
    refreshCandidate(cycleIndex);
  }
  let activeCount = cellCount - reservedCycleIndexes.size;
  let shortPathCount = paths.filter((path) =>
    path.length <= largeRectangleShortPathMaximumLength).length;
  while (activeCount > 0) {
    let selected: ReturnType<typeof candidatePath>;
    const heldCandidateIds: number[] = [];
    let equalLengthSelections = 0;
    const sampled = sampleTargetLength(weightedLengthMix, totalWeight, fallbackLengthMix, rng);
    for (let attempt = 0; attempt < snakeAttemptLimit; attempt += 1) {
      const candidateId = popRandomCandidate();
      if (candidateId === undefined) break;
      heldCandidateIds.push(candidateId);
      const candidate = candidatePath(candidateId, sampled);
      if (candidate === undefined || (selected !== undefined
        && candidate.indexes.length < selected.indexes.length)) {
        continue;
      }
      if (selected === undefined || candidate.indexes.length > selected.indexes.length) {
        selected = candidate;
        equalLengthSelections = 1;
      } else {
        equalLengthSelections += 1;
        if (nextInt(rng, 1, equalLengthSelections) === 1) selected = candidate;
      }
    }
    heldCandidateIds.forEach(addCandidate);
    if (selected === undefined) return null;
    paths.push([...selected.cells]);
    if (selected.cells.length <= largeRectangleShortPathMaximumLength) shortPathCount += 1;
    selected.indexes.forEach(deactivate);
    activeCount -= selected.indexes.length;
    effort.count += 1;
    if (effort.count > effortLimit) return null;
  }
  // A short segment sometimes has to be peeled to expose the next visible cycle edge.
  // Merge it only forward into a path that was still occupied at that peel step. Keeping
  // the earlier path's tip makes the known removal order valid while reducing short-path
  // frequency without rewriting the configured lengthMix weights.
  const pathSlots: Array<Cell[] | undefined> = [...paths];
  const ownerByCycleIndex = Array.from({ length: cellCount }, () => -1);
  pathSlots.forEach((path, pathIndex) => path?.forEach((cell) => {
    ownerByCycleIndex[cycleIndexOf(cell)] = pathIndex;
  }));
  let remainingPathCount = pathSlots.length;
  for (let pathIndex = protectedPathCount; pathIndex < pathSlots.length
    && shortPathCount / remainingPathCount > largeRectangleShortPathMergeThreshold;
    pathIndex += 1) {
    let path = pathSlots[pathIndex];
    while (path !== undefined && path.length < maximumLength) {
      const first = path[0];
      const second = path[1];
      const tail = path.at(-1);
      if (first === undefined || second === undefined || tail === undefined) break;
      const firstIndex = cycleIndexOf(first);
      const secondIndex = cycleIndexOf(second);
      const direction: -1 | 1 = wrapIndex(firstIndex + 1) === secondIndex ? 1 : -1;
      const tailIndex = cycleIndexOf(tail);
      const neighborIndex = wrapIndex(tailIndex + direction);
      const neighborOwner = ownerByCycleIndex[neighborIndex]!;
      const neighborPath = pathSlots[neighborOwner];
      if (neighborOwner <= pathIndex || neighborPath === undefined
        || path.length + neighborPath.length > maximumLength
        || (path.length > largeRectangleShortPathMaximumLength
          && neighborPath.length > largeRectangleShortPathMaximumLength)) {
        break;
      }
      const neighborFirst = neighborPath[0];
      const neighborLast = neighborPath.at(-1);
      const orientedNeighbor = neighborFirst !== undefined
        && cycleIndexOf(neighborFirst) === neighborIndex
        ? neighborPath
        : neighborLast !== undefined
          && cycleIndexOf(neighborLast) === neighborIndex
          ? [...neighborPath].reverse()
          : undefined;
      if (orientedNeighbor === undefined) break;
      const merged = [...path, ...orientedNeighbor];
      if (!isSelfRayValid(merged, true)) break;
      shortPathCount -= Number(path.length <= largeRectangleShortPathMaximumLength)
        + Number(neighborPath.length <= largeRectangleShortPathMaximumLength);
      // Both inputs have at least two cells, so their merged path is never short.
      pathSlots[pathIndex] = merged;
      pathSlots[neighborOwner] = undefined;
      remainingPathCount -= 1;
      orientedNeighbor.forEach((cell) => {
        ownerByCycleIndex[cycleIndexOf(cell)] = pathIndex;
      });
      path = merged;
      if (shortPathCount / remainingPathCount <= largeRectangleShortPathMergeThreshold) break;
    }
  }
  paths = pathSlots.filter((path): path is Cell[] => path !== undefined);
  const shortRatio = shortPathCount / paths.length;
  if (hasShortPathBand && (shortRatio < largeRectangleShortPathRatioMinimum
    || shortRatio > largeRectangleShortPathRatioMaximum)) return null;
  if (protectedPathCount === 0) return shuffle(rng, paths);
  return [
    ...shuffle(rng, paths.slice(protectedPathCount)),
    ...paths.slice(0, protectedPathCount),
  ];
}

function partitionLargeRectangle(
  mask: BoardMask,
  params: GenerationParams,
  rng: Rng,
  effort: PartitionEffort,
  effortLimit: number,
  reservedPaths: readonly SnakePath[],
): PartitionResult | null {
  const paths = partitionHamiltonianRectangle(
    { x: 0, y: 0, width: mask.width, height: mask.height },
    params,
    rng,
    effort,
    effortLimit,
    reservedPaths,
  );
  return paths === null ? null : {
    paths,
    ringMotifCount: 0,
    ringEnclosedCellCount: 0,
    chainPatchPathCount: reservedPaths.length,
  };
}

function partitionOnce(
  mask: BoardMask,
  params: GenerationParams,
  rng: Rng,
  effort: PartitionEffort,
  effortLimit: number,
): readonly SnakePath[] | null {
  const {
    weighted: weightedLengthMix,
    totalWeight,
    fallback: fallbackLengthMix,
    minimum,
    maximum,
  } = prepareLengthMix(params);
  if (mask.cells.length < minimum) {
    return null;
  }

  const cellsByKey = new Map(mask.cells.map((cell) => [cellKey(cell), cell]));
  const free = new Set(cellsByKey.keys());
  const paths: Cell[][] = [];

  while (free.size > 0) {
    const candidates = [...free]
      .map((cellKey) => cellsByKey.get(cellKey))
      .filter((cell): cell is Cell => cell !== undefined);
    const minimumDegree = Math.min(...candidates.map((cell) => neighbors(cell, free).length));
    const starts = candidates.filter((cell) => neighbors(cell, free).length === minimumDegree);
    const start = pickOne(rng, starts);
    let selected: Cell[] | null = null;
    let longest: Cell[] = [start];
    const growTracked = (initial: readonly Cell[], targetLength: number): Cell[] => {
      effort.count += 1;
      return grow(rng, initial, free, targetLength, params.turnBias);
    };

    for (let attempt = 0; attempt < snakeAttemptLimit && selected === null; attempt += 1) {
      const target = sampleTargetLength(weightedLengthMix, totalWeight, fallbackLengthMix, rng);
      let path = growTracked([start], target);
      if (effort.count > effortLimit) {
        return null;
      }
      if (path.length < minimum) {
        path = growTracked([...path].reverse(), target);
        if (effort.count > effortLimit) {
          return null;
        }
      }
      if (path.length < minimum && path.length > 1) {
        const trimCount = Math.min(4, path.length - 1);
        const prefix = path.slice(0, path.length - trimCount);
        path = growTracked(prefix, target);
        if (effort.count > effortLimit) {
          return null;
        }
      }
      if (path.length > longest.length) {
        longest = path;
      }
      selected = chooseUsablePrefix(path, free, cellsByKey, minimum);
    }

    if (selected === null && longest.length < minimum) {
      const merged = tryMergeShortPath(paths, longest, maximum);
      if (merged !== null) {
        paths[merged.index] = merged.path;
        longest.forEach((cell) => free.delete(cellKey(cell)));
        continue;
      }
    }
    if (selected === null) {
      return null;
    }
    selected.forEach((cell) => free.delete(cellKey(cell)));
    paths.push(selected);
  }

  return shuffle(rng, paths);
}

export function partitionMaskWithMetadata(
  mask: BoardMask,
  params: GenerationParams,
  rng: Rng,
  effort: PartitionEffort,
  effortLimit = Number.POSITIVE_INFINITY,
): PartitionResult | null {
  validateParams(params);
  const reservedPaths = edgeChainPatchPaths(mask, params, rng);
  if (mask.cells.length > largeRectangleHamiltonianPartitionThreshold
    && mask.cells.length === mask.width * mask.height) {
    return partitionLargeRectangle(mask, params, rng, effort, effortLimit, reservedPaths);
  }
  const reservedCells = new Set(reservedPaths.flatMap((path) => path.map(cellKey)));
  const organicMask = reservedCells.size === 0
    ? mask
    : { ...mask, cells: mask.cells.filter((cell) => !reservedCells.has(cellKey(cell))) };
  // These bounded restarts are the whole-partition counterpart of backing out
  // an earlier snake when its choices make a later component impossible.
  for (let attempt = 0; attempt < partitionRestartLimit; attempt += 1) {
    const result = partitionOnce(organicMask, params, rng, effort, effortLimit);
    if (result !== null) {
      return {
        paths: [...result, ...reservedPaths],
        ringMotifCount: 0,
        ringEnclosedCellCount: 0,
        chainPatchPathCount: reservedPaths.length,
      };
    }
    if (effort.count > effortLimit) {
      return null;
    }
  }
  return null;
}

export function partitionMask(
  mask: BoardMask,
  params: GenerationParams,
  rng: Rng,
  effort: PartitionEffort,
  effortLimit = Number.POSITIVE_INFINITY,
): readonly SnakePath[] | null {
  return partitionMaskWithMetadata(mask, params, rng, effort, effortLimit)?.paths ?? null;
}
